import { wrap } from "@reatom/core"

export function waitForGachaRevealMedia(
  media: HTMLImageElement | HTMLMediaElement,
  signal: AbortSignal,
) {
  return new Promise<void>((resolve, reject) => {
    const image = media instanceof HTMLImageElement
    const event = image ? "load" : "canplaythrough"
    const cleanup = () => {
      media.removeEventListener(event, ready)
      media.removeEventListener("error", failed)
      signal.removeEventListener("abort", aborted)
    }
    const ready = wrap(() => {
      cleanup()
      resolve()
    })
    const failed = wrap(() => {
      cleanup()
      reject(new Error(`Reveal media unavailable: ${media.src}`))
    })
    const aborted = wrap(() => {
      cleanup()
      reject(new Error("Reveal media loading cancelled or timed out"))
    })
    if (signal.aborted) {
      aborted()
      return
    }
    media.addEventListener(event, ready, { once: true })
    media.addEventListener("error", failed, { once: true })
    signal.addEventListener("abort", aborted, { once: true })
    if (
      image ? media.complete && media.naturalWidth > 0 : media.readyState >= 4
    )
      ready()
  })
}

export async function preloadGachaRevealImages(
  sources: readonly string[],
  signal: AbortSignal,
) {
  await wrap(
    Promise.all(
      [...new Set(sources.filter(Boolean))].map(async (src) => {
        const image = new Image()
        const loaded = waitForGachaRevealMedia(image, signal)
        image.src = src
        await wrap(loaded)
        await wrap(image.decode())
      }),
    ),
  )
}

export async function preloadGachaRevealVideos(
  sources: readonly string[],
  signal: AbortSignal,
) {
  await wrap(
    Promise.all(
      sources.map(async (src) => {
        const video = document.createElement("video")
        video.muted = true
        video.preload = "auto"
        const loaded = waitForGachaRevealMedia(video, signal)
        video.src = src
        video.load()
        await wrap(loaded)
      }),
    ),
  )
}
